import { useEffect, useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Box,
  Text,
  useToast,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  VStack,
  IconButton,
  Flex,
  useDisclosure,
} from '@chakra-ui/react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import styled from '@emotion/styled';
import { MdClear, MdUpload } from 'react-icons/md';

import TestResultsModal from './TestResultsModal';
import { acceptedMimeTypes, validationSchema } from './consts';
import { createExcelFileForSingleUserTest, prepareFormData, validateDataHasContent } from './utils';
import EmployeesApi from '../../../../../api/employees';
import { globalStyles } from '../../../../../theme/styles';

export default function TestUsersExcelModal({ isOpen, onClose, companyId }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [result, setResult] = useState(null);

  const formikRef = useRef(null);

  const toast = useToast();

  const {
    isOpen: isResultsModalOpen,
    onOpen: onResultsModalOpen,
    onClose: onResultsModalClose,
  } = useDisclosure();

  const initialValues = {
    companyId: companyId || '',
    phoneNumber: '',
    govId: '',
  };

  const onDrop = useCallback((acceptedFiles) => {
    setSelectedFile(acceptedFiles[0]);
  }, []);

  useEffect(() => {
    if (!companyId && isOpen) {
      toast({
        position: 'top',
        title: 'שגיאה בבחירת חברה',
        description: 'אנא נסה שנית מאוחר יותר',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [companyId, toast, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSelectedFile(null);
      setActiveTab(0);
      if (formikRef.current) {
        formikRef.current.resetForm();
      }
    }
  }, [isOpen]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: Object.values(acceptedMimeTypes).join(', '),
  });

  const handleExcelSubmission = async (formData, context) => {
    try {
      setIsLoading(true);

      const response = await EmployeesApi.testEmployeesExcel(formData);
      const responseData = response?.data || response;

      if (validateDataHasContent(responseData)) {
        toast({
          title: 'הצלחה',
          description: `נמצאו ${responseData?.summary?.general?.totalCount} נתונים`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });

        setResult(responseData);
        onResultsModalOpen();
        onClose();
      } else {
        toast({
          title: 'אין נתונים להצגה',
          description: 'נא לנסות שנית',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (error) {
      console.error(`Error ${context}:`, error);
      toast({
        title: 'שגיאה',
        description: `שגיאה בעת ${context}`,
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadSubmit = async () => {
    if (selectedFile) {
      const formData = prepareFormData(selectedFile, companyId);
      await handleExcelSubmission(formData, 'שליחת הקובץ');
    }
  };

  const handleFormSubmit = async (values) => {
    const fileResult = createExcelFileForSingleUserTest(values);
    const formData = prepareFormData(fileResult.file, companyId);
    await handleExcelSubmission(formData, 'שליחת הטופס');
  };

  const handleTabChange = (index) => {
    if (activeTab !== index) {
      if (index === 1) {
        setSelectedFile(null);
      } else if (index === 0 && formikRef.current) {
        formikRef.current.resetForm();
      }
      setActiveTab(index);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton position="absolute" left="8px" right="auto" top="8px" zIndex="1" />
          <ModalHeader>בדיקת ממשקים / משתמשים</ModalHeader>
          <ModalBody pb={6}>
            <Tabs isFitted variant="enclosed" index={activeTab} onChange={handleTabChange}>
              <TabList mb="1em">
                <Tab>העלאת קובץ Excel</Tab>
                <Tab>הזנה ידנית</Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
                    <Box position="relative" cursor={isLoading ? 'not-allowed' : 'pointer'}>
                      {selectedFile && (
                        <IconButton
                          icon={<MdClear />}
                          variant="outline"
                          size="sm"
                          position="absolute"
                          top="-10px"
                          right="-10px"
                          zIndex="1"
                          borderRadius="full"
                          onClick={clearFile}
                          backgroundColor="white"
                          isDisabled={isLoading}
                        />
                      )}
                      <UploadFileContainer
                        className="upload-file-container"
                        {...getRootProps()}
                        isDragActive={isDragActive}
                        isLoading={isLoading}
                      >
                        <input {...getInputProps()} />
                        {selectedFile ? (
                          <Text>{selectedFile.name}</Text>
                        ) : isDragActive ? (
                          <Text>גרור את קובץ של Excel נתוני העובדים לכאן</Text>
                        ) : (
                          <Text>בחר את קובץ של Excel נתוני העובדים</Text>
                        )}
                      </UploadFileContainer>
                    </Box>

                    <Flex justifyContent="flex-end" mt={2}>
                      <Button
                        onClick={handleUploadSubmit}
                        isLoading={isLoading && activeTab === 0}
                        variant="blue"
                        size="md"
                        isDisabled={!selectedFile}
                        leftIcon={<MdUpload />}
                        width="auto"
                      >
                        העלאת קובץ
                      </Button>
                    </Flex>
                  </VStack>
                </TabPanel>
                <TabPanel px={0}>
                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleFormSubmit}
                    innerRef={formikRef}
                    enableReinitialize
                  >
                    {({ isValid, dirty }) => (
                      <Form>
                        <VStack spacing={4} align="stretch">
                          <FormControl>
                            <FormLabel>מספר טלפון</FormLabel>
                            <Field
                              name="phoneNumber"
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  placeholder="מספר טלפון"
                                  width="100%"
                                  p="8px"
                                  type="number"
                                  disabled={isLoading}
                                />
                              )}
                            />
                            <Box minH="20px">
                              <ErrorMessage name="phoneNumber" component={ErrorMessageText} />
                            </Box>
                          </FormControl>

                          <FormControl>
                            <FormLabel>מספר ת.ז</FormLabel>
                            <Field
                              name="govId"
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  placeholder="מספר ת.ז"
                                  width="100%"
                                  p="8px"
                                  type="number"
                                  disabled={isLoading}
                                />
                              )}
                            />
                            <Box minH="20px">
                              <ErrorMessage name="govId" component={ErrorMessageText} />
                            </Box>
                          </FormControl>

                          <Flex justifyContent="flex-end">
                            <Button
                              type="submit"
                              isLoading={isLoading && activeTab === 1}
                              variant="blue"
                              size="md"
                              isDisabled={!(isValid && dirty)}
                              width="auto"
                            >
                              שליחת פרטים
                            </Button>
                          </Flex>
                        </VStack>
                      </Form>
                    )}
                  </Formik>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </Modal>
      <TestResultsModal
        isOpen={isResultsModalOpen}
        onClose={onResultsModalClose}
        data={result}
        companyId={companyId}
      />
    </>
  );
}

// ----------------------------------- Styled Components -----------------------------------

const ErrorMessageText = styled.div`
  font-size: 12px;
  color: red;
`;

const UploadFileContainer = styled.div`
  border: 1px solid
    ${({ isDragActive }) =>
      isDragActive ? globalStyles.colors.brand[300] : globalStyles.colors.secondaryGray[100]};
  pointer-events: ${({ isLoading }) => (isLoading ? 'none' : 'auto')};
  background-color: ${({ isLoading }) =>
    isLoading ? globalStyles.colors.secondaryGray[300] : 'white'};
  color: ${({ isLoading }) => (isLoading ? globalStyles.colors.secondaryGray[100] : 'black')};
  height: 120px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
`;
